import React, { useState, useEffect } from 'react';
import { Users, Search, Mail, Phone, MapPin, ShieldCheck, User as UserIcon } from 'lucide-react';

// TypeScript interface user data ke liye
interface User {
  srNo: number;
  userName: string;
  emailId: string;
  mobileNo: string;
  userRole: number;
  isActive: boolean;
  address: string | null;
}

const Customers: React.FC = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState<string>('');

  // Backend se users fetch karna
  useEffect(() => {
    const fetchUsers = async () => {
      try {
        // const response = await fetch('http://localhost:8080/api/users');
        const response = await fetch('https://sardar-jewellers.com/api/users');
        if (!response.ok) {
          throw new Error('Failed to fetch users data');
        }
        const data = await response.json();
        setUsers(data);
      } catch (err: any) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchUsers();
  }, []);

  // Search logic (Name, Email, ya Mobile se filter karne ke liye)
  const filteredUsers = users.filter((user) => 
    user.userName.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.emailId.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.mobileNo.includes(searchTerm)
  );

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen bg-gray-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-yellow-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-8 text-center text-red-600 bg-red-50 min-h-screen">
        <h2 className="text-2xl font-bold mb-2">Error Loading Customers</h2>
        <p>{error}</p>
      </div>
    );
  }

  return (
    <div className="p-8 bg-gray-100 min-h-screen">
      {/* Header Section */}
      <div className="flex flex-col md:flex-row justify-between items-center mb-8 gap-4">
        <div>
          <h1 className="text-3xl font-extrabold text-gray-900 flex items-center">
            <Users className="w-8 h-8 text-yellow-600 mr-3" />
            Customers Management
          </h1>
          <p className="text-gray-600 mt-1">View and manage all registered users.</p>
        </div>

        {/* Search Bar */}
        <div className="relative w-full md:w-1/3">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <Search className="h-5 w-5 text-gray-400" />
          </div>
          <input
            type="text"
            className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg leading-5 bg-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500 sm:text-sm transition duration-150 ease-in-out shadow-sm"
            placeholder="Search by name, email or mobile..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
      </div>

      {/* Table Section */}
      <div className="bg-white rounded-xl shadow-lg overflow-hidden border border-gray-200">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th scope="col" className="px-6 py-4 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">
                  Customer
                </th>
                <th scope="col" className="px-6 py-4 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">
                  Contact Info
                </th>
                <th scope="col" className="px-6 py-4 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">
                  Role
                </th>
                <th scope="col" className="px-6 py-4 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">
                  Status
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredUsers.length > 0 ? (
                filteredUsers.map((user) => (
                  <tr key={user.srNo} className="hover:bg-gray-50 transition-colors">
                    
                    {/* User Profile / Name */}
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 h-10 w-10 bg-yellow-100 rounded-full flex items-center justify-center text-yellow-600 font-bold text-lg">
                          {user.userName.charAt(0).toUpperCase()}
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-semibold text-gray-900">{user.userName}</div>
                          {user.address && (
                            <div className="text-xs text-gray-500 flex items-center mt-1">
                              <MapPin className="w-3 h-3 mr-1" /> {user.address}
                            </div>
                          )}
                        </div>
                      </div>
                    </td>

                    {/* Contact Info */}
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900 flex items-center mb-1">
                        <Mail className="w-4 h-4 mr-2 text-gray-400" /> {user.emailId}
                      </div>
                      <div className="text-sm text-gray-500 flex items-center">
                        <Phone className="w-4 h-4 mr-2 text-gray-400" /> +91 {user.mobileNo}
                      </div>
                    </td>

                    {/* Role */}
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-3 py-1 inline-flex text-xs leading-5 font-semibold rounded-full flex items-center w-fit ${
                        user.userRole === 1 
                          ? 'bg-purple-100 text-purple-800' // 1 usually means Admin
                          : 'bg-blue-100 text-blue-800'     // 0 usually means regular Customer
                      }`}>
                        {user.userRole === 1 ? <ShieldCheck className="w-3 h-3 mr-1" /> : <UserIcon className="w-3 h-3 mr-1" />}
                        {user.userRole === 1 ? 'Admin' : 'Customer'}
                      </span>
                    </td>

                    {/* Status */}
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-3 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
                        user.isActive 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-red-100 text-red-800'
                      }`}>
                        {user.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </td>

                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={4} className="px-6 py-10 text-center text-gray-500">
                    No customers found. Try a different search term.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        {/* Simple Footer/Pagination info */}
        <div className="bg-gray-50 px-6 py-3 border-t border-gray-200">
          <p className="text-sm text-gray-700">
            Showing <span className="font-semibold">{filteredUsers.length}</span> total users.
          </p>
        </div>
      </div>
    </div>
  );
};

export default Customers;